import {useEffect, useState} from 'react'
import {useLayout} from '../../../../_metronic/layout/core'
import {ProductUnit, createUnit, editUnit} from './api'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import toast from 'react-hot-toast'

// ─── Validation Schema ────────────────────────────────────────────────────────

const unitSchema = Yup.object().shape({
  unit_name: Yup.string()
    .required('Unit name is required')
    .matches(/^[a-zA-Z0-9\-' ]{1,50}$/, {
      message:
        'Unit name must be 1-50 characters and can only contain letters, numbers, hyphens, apostrophes and spaces',
    }),
  abbreviation: Yup.string()
    .required('Abbreviation is required')
    .matches(/^[a-zA-Z0-9]{1,10}$/, {
      message: 'Abbreviation must be 1-10 alphanumeric characters',
    }),
})

// ─── Types ────────────────────────────────────────────────────────────────────

interface UnitFormValues {
  unit_name: string
  abbreviation: string
}

// ─── Component ────────────────────────────────────────────────────────────────

export const CreateEditUnit: React.FC<{
  setShowForm: React.Dispatch<React.SetStateAction<boolean>>
  setEditUnit: React.Dispatch<React.SetStateAction<ProductUnit | undefined>>
  editUnitData?: ProductUnit
  setShouldRefreshUnits: React.Dispatch<React.SetStateAction<boolean>>
}> = ({setShowForm, editUnitData, setShouldRefreshUnits, setEditUnit}) => {
  const {company} = useLayout()
  const [loading, setLoading] = useState(false)

  const initialValues: UnitFormValues = {
    unit_name: editUnitData?.unit_name || '',
    abbreviation: editUnitData?.abbreviation || '',
  }

  const formik = useFormik<UnitFormValues>({
    initialValues,
    validationSchema: unitSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      setLoading(true)
      const body = {
        company_id: company?.value,
        unit_name: values.unit_name,
        abbreviation: values.abbreviation,
        previous_unit_name: editUnitData?.unit_name,
      }

      if (!editUnitData) {
        await createUnit(body)
          .then((res) => {
            res?.detail && toast.success('The requested unit has been added successfully.')
            formik.resetForm()
            setShouldRefreshUnits(true)
          })
          .catch((err) => toast.error(err?.detail || err.message))
          .finally(() => {
            setLoading(false)
            setShowForm(false)
          })
      } else {
        await editUnit(body)
          .then((res) => {
            res?.detail && toast.success('Unit has been updated successfully.')
            formik.resetForm()
            setShouldRefreshUnits(true)
          })
          .catch((err) => toast.error(err?.detail || err.message))
          .finally(() => {
            setLoading(false)
            setShowForm(false)
          })
      }
    },
  })

  // Pre-populate fields when editing
  useEffect(() => {
    if (editUnitData) {
      formik.setFieldValue('unit_name', editUnitData.unit_name)
      formik.setFieldValue('abbreviation', editUnitData.abbreviation)
    }
  }, [editUnitData])

  return (
    <div className='card'>
      <div
        className='card-header border-0'
        data-bs-target='#kt_product_unit_form'
        aria-expanded='true'
        aria-controls='kt_product_unit_form'
      >
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>{!!editUnitData ? 'Update Unit' : 'Add New Unit'}</h3>
        </div>
      </div>

      <form onSubmit={formik.handleSubmit} noValidate className='form'>
        <div id='kt_product_unit_form' className='collapse show'>
          <div className='p-8 pb-0'>
            {/* Unit Name */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Unit Name</label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Unit Name'
                  {...formik.getFieldProps('unit_name')}
                />
                {formik.touched.unit_name && formik.errors.unit_name && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.unit_name}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Abbreviation */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Abbreviation</label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Abbreviation'
                  {...formik.getFieldProps('abbreviation')}
                />
                {formik.touched.abbreviation && formik.errors.abbreviation && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.abbreviation}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className='card-footer d-flex justify-content-end px-9 border-0 pb-7 pt-0'>
          <button
            type='button'
            className='btn btn-secondary'
            style={{marginRight: '10px'}}
            onClick={() => {
              formik.resetForm()
              setEditUnit(undefined)
              setShowForm(false)
              setShouldRefreshUnits(false)
            }}
          >
            Cancel
          </button>
          <button
            type='submit'
            className='btn btn-primary ml-2'
            disabled={loading || !formik.isValid}
          >
            {!loading && !!editUnitData ? 'Save Unit' : 'Add Unit'}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Please wait...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}
